// appliances.js — appliance library with capabilities, organized by category

export const APPLIANCE_LIBRARY = [
  // Cookware
  { id: "dutch_oven", name: "Dutch Oven", category: "cookware", icon: "🍲",
    capabilities: ["sear", "boil", "simmer", "braise", "slow_cook", "fry_shallow", "bake"] },
  { id: "cast_iron", name: "Cast Iron Skillet", category: "cookware", icon: "🍳",
    capabilities: ["sear", "saute", "fry_shallow", "griddle", "bake", "broil"] },
  { id: "nonstick_pan", name: "Nonstick Pan", category: "cookware", icon: "🍳",
    capabilities: ["saute", "egg_cook", "fry_shallow"] },
  { id: "stainless_pan", name: "Stainless Steel Pan", category: "cookware", icon: "🍳",
    capabilities: ["saute", "sear", "fry_shallow", "fond_build"] },
  { id: "wok", name: "Wok", category: "cookware", icon: "🥘",
    capabilities: ["stir_fry", "sear", "deep_fry", "steam"] },
  { id: "sheet_pan", name: "Sheet Pan", category: "cookware", icon: "🥖",
    capabilities: ["roast", "bake", "broil"] },
  { id: "saucepan", name: "Saucepan", category: "cookware", icon: "🥣",
    capabilities: ["boil", "simmer", "reduce"] },
  { id: "stockpot", name: "Stock Pot", category: "cookware", icon: "🍜",
    capabilities: ["boil", "simmer", "stock_make"] },
  // Major appliances
  { id: "oven_standard", name: "Oven", category: "major", icon: "🔥",
    capabilities: ["bake", "roast", "broil"] },
  { id: "oven_convection", name: "Convection Oven", category: "major", icon: "🔥",
    capabilities: ["bake", "roast", "broil", "convection_bake", "dehydrate"] },
  { id: "induction", name: "Induction Cooktop", category: "major", icon: "♨️",
    capabilities: ["precise_heat"] },
  { id: "gas_range", name: "Gas Range", category: "major", icon: "🔥",
    capabilities: ["high_heat", "char"] },
  // Countertop
  { id: "instant_pot", name: "Instant Pot", category: "countertop", icon: "⏱️",
    capabilities: ["pressure_cook", "slow_cook", "saute", "steam", "rice_cook", "yogurt_make"] },
  { id: "air_fryer", name: "Air Fryer", category: "countertop", icon: "💨",
    capabilities: ["roast", "convection_bake", "fry_air"] },
  { id: "slow_cooker", name: "Slow Cooker", category: "countertop", icon: "🐢",
    capabilities: ["slow_cook", "warm_hold"] },
  { id: "rice_cooker", name: "Rice Cooker", category: "countertop", icon: "🍚",
    capabilities: ["rice_cook", "steam", "warm_hold"] },
  { id: "toaster_oven", name: "Toaster Oven", category: "countertop", icon: "🍞",
    capabilities: ["toast", "broil", "bake_small"] },
  { id: "microwave", name: "Microwave", category: "countertop", icon: "📡",
    capabilities: ["reheat", "melt", "steam_quick"] },
  { id: "sous_vide", name: "Sous Vide Circulator", category: "countertop", icon: "🌡️",
    capabilities: ["sous_vide", "precise_heat"] },
  // Mixing & prep
  { id: "stand_mixer", name: "Stand Mixer", category: "prep", icon: "🌀",
    capabilities: ["mix_stand", "knead_dough", "whip_cream"] },
  { id: "hand_mixer", name: "Hand Mixer", category: "prep", icon: "🥄",
    capabilities: ["mix_hand", "whip_cream"] },
  { id: "food_processor", name: "Food Processor", category: "prep", icon: "🔪",
    capabilities: ["food_process", "knead_dough", "puree"] },
  { id: "blender_high", name: "High-Speed Blender", category: "prep", icon: "🌪️",
    capabilities: ["blend_high_speed", "puree", "ice_crush"] },
  { id: "immersion_blender", name: "Immersion Blender", category: "prep", icon: "🥄",
    capabilities: ["blend_immersion", "puree"] },
  // Coffee — Josh's specific gear
  { id: "fellow_aiden", name: "Fellow Aiden", category: "coffee", icon: "☕",
    capabilities: ["drip_brew", "precise_brew", "bloom_control"] },
  { id: "ode_gen2", name: "Fellow Ode Gen 2", category: "coffee", icon: "⚙️",
    capabilities: ["burr_grind", "filter_grind"] },
  { id: "espresso_machine", name: "Espresso Machine", category: "coffee", icon: "☕",
    capabilities: ["espresso_pull"] },
  { id: "pour_over", name: "Pour Over (V60/Chemex)", category: "coffee", icon: "🫖",
    capabilities: ["pour_over"] },
  { id: "french_press", name: "French Press", category: "coffee", icon: "🫖",
    capabilities: ["french_press"] },
  { id: "aeropress", name: "AeroPress", category: "coffee", icon: "🫖",
    capabilities: ["pour_over", "french_press", "aeropress"] },
  // Tools
  { id: "kitchen_scale", name: "Kitchen Scale", category: "tool", icon: "⚖️",
    capabilities: ["scale_weigh"] },
  { id: "probe_thermo", name: "Probe Thermometer", category: "tool", icon: "🌡️",
    capabilities: ["thermometer_probe"] },
  { id: "microplane", name: "Microplane", category: "tool", icon: "🔪",
    capabilities: ["zest", "fine_grate"] },
  { id: "mandoline", name: "Mandoline", category: "tool", icon: "🔪",
    capabilities: ["mandoline_slice"] },
  { id: "mortar_pestle", name: "Mortar & Pestle", category: "tool", icon: "🥣",
    capabilities: ["crush_grind"] },
];

export const APPLIANCE_CATEGORIES = {
  cookware: { label: "Cookware", order: 1 },
  major: { label: "Major Appliances", order: 2 },
  countertop: { label: "Countertop", order: 3 },
  prep: { label: "Mixing & Prep", order: 4 },
  coffee: { label: "Coffee", order: 5 },
  tool: { label: "Tools", order: 6 },
};

export function getCapabilities(ownedIds) {
  const caps = new Set();
  for (const app of APPLIANCE_LIBRARY) {
    if (ownedIds.includes(app.id)) {
      app.capabilities.forEach(c => caps.add(c));
    }
  }
  return caps;
}

export function applianceById(id) {
  return APPLIANCE_LIBRARY.find(a => a.id === id);
}

// Loose name → library_id resolver for AI-imported recipes that come back
// with `appliances_needed: ["Dutch Oven", "stand mixer"]` instead of canonical
// IDs. Returns null when no good match is found.
export function applianceIdByName(rawName) {
  if (!rawName) return null;
  const norm = String(rawName).toLowerCase().trim().replace(/[^a-z0-9]+/g, " ").trim();
  if (!norm) return null;

  // Exact name match (case-insensitive, punctuation-insensitive)
  for (const a of APPLIANCE_LIBRARY) {
    const aName = a.name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    if (aName === norm) return a.id;
  }
  // Substring match either direction (e.g. "8-quart dutch oven" → "dutch oven")
  for (const a of APPLIANCE_LIBRARY) {
    const aName = a.name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    if (norm.includes(aName) || aName.includes(norm)) return a.id;
  }
  // ID-form input ("dutch_oven" → matches id directly)
  const idCandidate = norm.replace(/\s+/g, "_");
  const direct = APPLIANCE_LIBRARY.find(a => a.id === idCandidate);
  return direct?.id || null;
}

// Map an array of name-like strings to canonical library_ids, dropping any
// that don't resolve. Dedupes the result.
export function resolveApplianceNames(names) {
  const out = new Set();
  for (const n of names || []) {
    const id = applianceIdByName(n);
    if (id) out.add(id);
  }
  return [...out];
}

export function applianceByCategory() {
  const grouped = {};
  for (const app of APPLIANCE_LIBRARY) {
    if (!grouped[app.category]) grouped[app.category] = [];
    grouped[app.category].push(app);
  }
  return grouped;
}
